import classNames from 'classnames';
import { appInsight, collectProperties } from 'components/appInsight';
import { shallowEqual } from 'fast-equals';
import { setIsBetGeniusLoaded, setScoreboardHasError } from 'features/sport/sportSlice';
import { ScoreBoardProvider } from 'features/sport/types';
import { betRadar, betRadarDestroy } from 'features/sport/utils/betRadar/betradar';
import { StatisticType } from 'features/sport/utils/betRadar/statisticTypeEnum';
import { getCurrentLocale } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { RootState, useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { getNonce } from 'lib/policy';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useCallback, useEffect, useRef } from 'react';
import { isServerSide } from 'utility/constant';
import { isMatch } from 'utility/functions';
import styles from './SportResultsAndInfoContent.module.scss';

export type StatsBaseProps = {
  isOpen?: boolean;
  className?: string;
  id?: number;
};

export type OtherStatsProps = StatsBaseProps & {
  providerID: number | string;
  type: StatisticType.Avvenimento | StatisticType.AvvenimentoLive | StatisticType.Manifestazione;
};

export type PlayerStatsProps = StatsBaseProps & {
  type: StatisticType.Player;
  playerId: number;
  seasonId: number;
  uniqueTournamentId: number;
  teamUid: number;
};

export type CarStatsProps = StatsBaseProps & {
  type: StatisticType.Cars;
  stageId: number;
};

export type SportResultsAndInfoContentProps = PlayerStatsProps | OtherStatsProps | CarStatsProps;

const isPlayer = (props: SportResultsAndInfoContentProps): props is PlayerStatsProps => {
  return props.type === StatisticType.Player;
};

// NOTE: EXPLICIT ANY FOR TYPE CHECK
const isCar = (props: any): props is CarStatsProps => {
  return !!props.stageId;
};

export function SportResultsAndInfoContent(_props: CarStatsProps): JSX.Element;
export function SportResultsAndInfoContent(_props: OtherStatsProps): JSX.Element;
export function SportResultsAndInfoContent(_props: PlayerStatsProps): JSX.Element;
export function SportResultsAndInfoContent(props: SportResultsAndInfoContentProps) {
  const dispatch = useAppDispatch();

  const { isReady } = useRouter();
  const { type, isOpen, className, id } = props;
  const { isMobile } = useWindowSize();

  const ref = useRef<HTMLDivElement>(null);
  const elementClassName = id !== undefined ? `.sr-widget-1-${id}` : '.sr-widget-1';

  const { isBetGeniusLoaded } = useTypedSelector(({ sport }: RootState) => sport ?? {}, shallowEqual);

  const retrieveSettings = useCallback(
    (statisticType: StatisticType) => {
      switch (statisticType) {
        case StatisticType.Player:
          if (isPlayer(props)) {
            const { playerId, seasonId, teamUid, uniqueTournamentId } = props;
            return {
              playerId,
              seasonId,
              teamUid,
              uniqueTournamentId,
            };
          }
          throw new Error(`Trying to retrieve settings for a player but the props are not of type PlayerStatsProps`);
        case StatisticType.Avvenimento:
          return {
            disableMoreStats: true,
            disableExpandButton: true,
            branding: {
              tabs: {
                option: 'iconText',
                iconPosition: 'start',
                variant: 'fullWidth',
              },
            },
            matchId: (props as OtherStatsProps).providerID,
          };
        case StatisticType.Manifestazione:
          return {
            uniqueTournamentId: (props as OtherStatsProps).providerID,
            disableMoreStats: true,
            isExpanded: true,
            isMobile,
            disableEventTicker: true,
            disableCompare: true,
          };
        case StatisticType.AvvenimentoLive:
          if (isCar(props)) {
            return {
              stageId: props.stageId,
              layout: 'double',
            };
          }
          return {
            layout: 'double',
            scoreboard: 'extended',
            momentum: 'extended',
            tabsPosition: 'top',
            demoStream: false,
            streamToggle: 'onPitchButton',
            matchId: id,
            collapseTo: 'momentum',
            activeSwitcher: 'scoreDetails',
          };
        default:
          return {};
      }
    },
    [id, props, isMobile]
  );

  const betRadarLoaded = () => {
    dispatch(setIsBetGeniusLoaded(true));
  };

  useEffect(() => {
    if (isServerSide) return;
    if (isBetGeniusLoaded && !ref.current?.hasChildNodes()) {
      try {
        betRadar({
          className: elementClassName,
          type,
          settings: {
            ...retrieveSettings(type),
            onTrack: (param, param2) => {
              if (param === 'license_error' || param2?.error === 'UnsupportedSportError') {
                dispatch(setScoreboardHasError({ hasError: true }));
              }
              if (param === 'data_change') {
                if (param2?.error != null) {
                  dispatch(setScoreboardHasError({ hasError: true, provider: ScoreBoardProvider.BetGenius }));
                }
              }
            },
          },
        });
      } catch (error) {
        appInsight?.trackException({
          error,
          properties: collectProperties(true, { type }),
          id: `${props.id}`,
        });
        dispatch(setScoreboardHasError({ hasError: true }));
      }
    }
  }, [type, elementClassName, isBetGeniusLoaded, props.id, dispatch, retrieveSettings]);

  useEffect(() => {
    return () => {
      if (isServerSide) return;

      if (isBetGeniusLoaded) {
        betRadarDestroy(elementClassName);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isReady && !isBetGeniusLoaded && isMatch(process.env.NEXT_PUBLIC_BETRADAR_URL, '^http') && (
        <Script
          strategy="lazyOnload"
          src={process.env.NEXT_PUBLIC_BETRADAR_URL}
          nonce={getNonce()}
          onReady={betRadarLoaded}
          data-sr-language={getCurrentLocale()}
        />
      )}
      <div
        className={classNames(styles.content, styles[type.replace('.', '-')], className ?? '', {
          [styles.visible]: isOpen && isBetGeniusLoaded,
        })}
      >
        <div ref={ref} className={id !== undefined ? `sr-widget-1-${id}` : 'sr-widget-1'}></div>
      </div>
    </>
  );
}
